import { useAuth0 } from "@appretio/auth";
import { PropsWithChildren, useMemo } from "react";
import { createApiClient } from "../client";
import { ApiContext, ApiTokenContext } from "./ApiClientContext";
import { withReauthFallback } from "./withReauthFallback";

const AUTH_DISABLED = import.meta.env.VITE_AUTH_DISABLED === "true";

/**
 * Stub token getter used when `VITE_AUTH_DISABLED=true`. The API itself
 * also bypasses auth in that mode (`APPRETIO_AUTH_DISABLED=true`), so no
 * Bearer header is needed on requests; we hand back an empty string and
 * let the API client skip the `Authorization` header (the middleware
 * branches on truthiness of the returned token).
 */
async function noopAccessToken(): Promise<string> {
  return "";
}

/**
 * Reusable API provider component.
 * Provides the api client to its children, using Auth0 for token acquisition.
 *
 * When `VITE_AUTH_DISABLED=true`, skips the Auth0 hook (which would
 * synchronously fail without an `Auth0Provider` ancestor — see
 * `packages/auth/src/components/AuthProvider.tsx`) and supplies a no-op
 * token getter. Pairs with the API-side `APPRETIO_AUTH_DISABLED` flag.
 */
export function ApiClientProvider({ children }: PropsWithChildren) {
  if (AUTH_DISABLED) {
    return <BypassApiClientProvider>{children}</BypassApiClientProvider>;
  }
  return <RealApiClientProvider>{children}</RealApiClientProvider>;
}

function RealApiClientProvider({ children }: PropsWithChildren) {
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();

  const client = useMemo(
    () =>
      createApiClient(
        withReauthFallback(getAccessTokenSilently, loginWithRedirect)
      ),
    [getAccessTokenSilently, loginWithRedirect]
  );

  return (
    <ApiContext.Provider value={client}>
      <ApiTokenContext.Provider value={getAccessTokenSilently}>
        {children}
      </ApiTokenContext.Provider>
    </ApiContext.Provider>
  );
}

function BypassApiClientProvider({ children }: PropsWithChildren) {
  const client = useMemo(() => createApiClient(noopAccessToken), []);
  return (
    <ApiContext.Provider value={client}>
      <ApiTokenContext.Provider value={noopAccessToken}>
        {children}
      </ApiTokenContext.Provider>
    </ApiContext.Provider>
  );
}
